import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { Plus, ChevronDown, ChevronUp, Pencil, Bed as BedIcon, AlertTriangle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  getWards, createWard, updateWard,
  getBeds, createBed, updateBed,
  type Ward, type Bed,
} from '@/lib/ipdApi';

const WARD_TYPES = ['general','private','semi_private','icu','maternity','pediatric','other'] as const;
const BED_STATUSES = ['available','maintenance','reserved'] as const;

// ─── Ward form ────────────────────────────────────────────────────────────────

interface WardForm { name: string; ward_type: string; default_rent_per_day: string; notes: string; is_active: boolean; }
const emptyWard = (): WardForm => ({ name: '', ward_type: 'general', default_rent_per_day: '0', notes: '', is_active: true });

// ─── Bed form ─────────────────────────────────────────────────────────────────

interface BedForm { bed_number: string; rent_per_day: string; notes: string; status: string; is_active: boolean; }
const emptyBed = (): BedForm => ({ bed_number: '', rent_per_day: '', notes: '', status: 'available', is_active: true });

// Generates sequential bed numbers from a starting number, e.g. "A-1" x3 -> ["A-1","A-2","A-3"]
function generateBedNumbers(start: string, count: number): string[] {
  const trimmed = start.trim();
  if (count <= 1) return [trimmed];
  const m = trimmed.match(/^(.*?)(\d+)$/);
  if (!m) return [trimmed];
  const [, prefix, numStr] = m;
  const width = numStr.length;
  const startNum = parseInt(numStr, 10);
  return Array.from({ length: count }, (_, i) =>
    prefix + String(startNum + i).padStart(width, '0')
  );
}

// ─── Shared select ────────────────────────────────────────────────────────────

function Sel({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: readonly string[] }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring">
      {options.map(o => <option key={o} value={o}>{o.replace('_', ' ')}</option>)}
    </select>
  );
}

// ─── Bed row ──────────────────────────────────────────────────────────────────

function BedRow({ bed, wardId, onRefresh }: { bed: Bed; wardId: string; onRefresh: () => void }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<BedForm>({
    bed_number:  bed.bed_number,
    rent_per_day: bed.rent_per_day != null ? String(bed.rent_per_day) : '',
    notes:  bed.notes || '',
    status: bed.status,
    is_active: bed.is_active !== 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const occupiedByAdmission = !!bed.current_admission_id;

  const save = async () => {
    setError('');
    if (occupiedByAdmission && !form.is_active) {
      setError('Cannot deactivate a bed with a current admission');
      return;
    }
    setSaving(true);
    try {
      await updateBed(bed.id, {
        bed_number:  form.bed_number,
        rent_per_day: form.rent_per_day ? parseFloat(form.rent_per_day) : null,
        notes:  form.notes || null,
        status: form.status,
        is_active: form.is_active ? 1 : 0,
      });
      setOpen(false);
      onRefresh();
    } catch (e: any) {
      setError(e?.message || 'Failed to save bed');
    } finally {
      setSaving(false);
    }
  };

  const statusColors: Record<string, string> = {
    available:   'bg-green-100 text-green-700',
    occupied:    'bg-blue-100 text-blue-700',
    maintenance: 'bg-amber-100 text-amber-700',
    reserved:    'bg-purple-100 text-purple-700',
  };

  return (
    <>
      <div className={cn('flex items-center justify-between px-3 py-2 border-b last:border-0 hover:bg-muted/30', bed.is_active === 0 && 'opacity-50')}>
        <div className="flex items-center gap-3">
          <BedIcon className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-sm font-medium">Bed {bed.bed_number}</span>
          {bed.current_patient_name && (
            <span className="text-xs text-muted-foreground">· {bed.current_patient_name}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {bed.rent_per_day != null && (
            <span className="text-xs text-muted-foreground">₹{bed.rent_per_day}/day</span>
          )}
          {bed.is_active === 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">Inactive</span>
          )}
          <span className={cn('text-xs px-2 py-0.5 rounded-full capitalize', statusColors[bed.occupancy_status] ?? '')}>
            {bed.occupancy_status}
          </span>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setOpen(true)}>
            <Pencil className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Edit Bed</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Bed Number</label>
              <Input value={form.bed_number} onChange={e => setForm(f => ({ ...f, bed_number: e.target.value }))} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Rent/Day (₹) — blank = use ward default</label>
              <Input type="number" value={form.rent_per_day} onChange={e => setForm(f => ({ ...f, rent_per_day: e.target.value }))} className="h-8 text-sm" placeholder="Use ward default" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Status</label>
              {occupiedByAdmission ? (
                <p className="text-xs text-muted-foreground py-1.5">
                  Occupied by {bed.current_patient_name || 'a current admission'} — status is managed automatically.
                </p>
              ) : (
                <Sel value={form.status} onChange={v => setForm(f => ({ ...f, status: v }))} options={BED_STATUSES} />
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
              <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="h-8 text-sm" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-xs font-medium">Active</label>
                <p className="text-xs text-muted-foreground">Inactive beds are hidden from admission &amp; bed-count totals.</p>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))}
                disabled={occupiedByAdmission}
              />
            </div>
            {error && <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Ward card ────────────────────────────────────────────────────────────────

function WardCard({ ward, onRefresh }: { ward: Ward; onRefresh: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [beds, setBeds] = useState<Bed[]>([]);
  const [loadingBeds, setLoadingBeds] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [addBedOpen, setAddBedOpen] = useState(false);
  const [wardForm, setWardForm] = useState<WardForm>({
    name: ward.name, ward_type: ward.ward_type,
    default_rent_per_day: String(ward.default_rent_per_day), notes: ward.notes || '',
    is_active: ward.is_active !== 0,
  });
  const [bedForm, setBedForm] = useState<BedForm>(emptyBed());
  const [bedQuantity, setBedQuantity] = useState('1');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadBeds = useCallback(async () => {
    setLoadingBeds(true);
    const data = await getBeds(ward.id);
    setBeds(data ?? []);
    setLoadingBeds(false);
  }, [ward.id]);

  useEffect(() => { if (expanded) loadBeds(); }, [expanded, loadBeds]);

  const saveWard = async () => {
    setError('');
    setSaving(true);
    try {
      await updateWard(ward.id, {
        name: wardForm.name, ward_type: wardForm.ward_type,
        default_rent_per_day: parseFloat(wardForm.default_rent_per_day) || 0,
        notes: wardForm.notes || null, is_active: wardForm.is_active ? 1 : 0,
      });
      setEditOpen(false); onRefresh();
    } catch (e: any) {
      setError(e?.message || 'Failed to save ward');
    } finally {
      setSaving(false);
    }
  };

  const saveBed = async () => {
    setError('');
    if (!bedForm.bed_number.trim()) { setError('Bed number is required'); return; }
    const count = Math.min(Math.max(parseInt(bedQuantity, 10) || 1, 1), 50);
    const bedNumbers = generateBedNumbers(bedForm.bed_number, count);
    setSaving(true);
    try {
      for (const bed_number of bedNumbers) {
        await createBed(ward.id, {
          bed_number,
          rent_per_day: bedForm.rent_per_day ? parseFloat(bedForm.rent_per_day) : null,
          notes: bedForm.notes || null,
        });
      }
      setAddBedOpen(false); setBedForm(emptyBed()); setBedQuantity('1'); loadBeds(); onRefresh();
    } catch (e: any) {
      setError(e?.message || 'Failed to add bed(s)');
      loadBeds(); onRefresh();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={cn('rounded-md border bg-card', ward.is_active === 0 && 'opacity-60')}>
      {/* Ward header */}
      <div className="flex items-center justify-between px-4 py-3">
        <button className="flex items-center gap-3 flex-1 text-left" onClick={() => setExpanded(e => !e)}>
          <div>
            <span className="font-medium text-sm">{ward.name}</span>
            <span className="ml-2 text-xs text-muted-foreground capitalize">{ward.ward_type.replace('_', ' ')}</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground ml-4">
            <span>{ward.occupied_beds}/{ward.bed_count} occupied</span>
            <span>₹{ward.default_rent_per_day}/day default</span>
          </div>
          {ward.is_active === 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">Inactive</span>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 ml-auto text-muted-foreground" /> : <ChevronDown className="w-4 h-4 ml-auto text-muted-foreground" />}
        </button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 ml-2" onClick={() => { setError(''); setEditOpen(true); }}>
          <Pencil className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* Beds */}
      {expanded && (
        <div className="border-t">
          {loadingBeds ? (
            <div className="px-4 py-3 text-xs text-muted-foreground">Loading beds…</div>
          ) : beds.length === 0 ? (
            <div className="px-4 py-3 text-xs text-muted-foreground">No beds added yet.</div>
          ) : (
            beds.map(b => <BedRow key={b.id} bed={b} wardId={ward.id} onRefresh={loadBeds} />)
          )}
          <div className="px-3 py-2 border-t">
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setBedForm(emptyBed()); setBedQuantity('1'); setError(''); setAddBedOpen(true); }}>
              <Plus className="w-3 h-3 mr-1" /> Add Bed
            </Button>
          </div>
        </div>
      )}

      {/* Edit ward dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Edit Ward</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Name</label>
              <Input value={wardForm.name} onChange={e => setWardForm(f => ({ ...f, name: e.target.value }))} className="h-8 text-sm" /></div>
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Type</label>
              <Sel value={wardForm.ward_type} onChange={v => setWardForm(f => ({ ...f, ward_type: v }))} options={WARD_TYPES} /></div>
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Default Rent/Day (₹)</label>
              <Input type="number" value={wardForm.default_rent_per_day} onChange={e => setWardForm(f => ({ ...f, default_rent_per_day: e.target.value }))} className="h-8 text-sm" /></div>
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
              <Input value={wardForm.notes} onChange={e => setWardForm(f => ({ ...f, notes: e.target.value }))} className="h-8 text-sm" /></div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-xs font-medium">Active</label>
                <p className="text-xs text-muted-foreground">Inactive wards are hidden from new admissions.</p>
              </div>
              <Switch
                checked={wardForm.is_active}
                onCheckedChange={v => setWardForm(f => ({ ...f, is_active: v }))}
              />
            </div>
            {error && <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={saveWard} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add bed dialog */}
      <Dialog open={addBedOpen} onOpenChange={setAddBedOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add Bed — {ward.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  {parseInt(bedQuantity, 10) > 1 ? 'First Bed Number *' : 'Bed Number *'}
                </label>
                <Input value={bedForm.bed_number} onChange={e => setBedForm(f => ({ ...f, bed_number: e.target.value }))} placeholder="e.g. 101, A-1" className="h-8 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Quantity</label>
                <Input type="number" min="1" max="50" value={bedQuantity} onChange={e => setBedQuantity(e.target.value)} className="h-8 text-sm" />
              </div>
            </div>
            {parseInt(bedQuantity, 10) > 1 && (
              <p className="text-xs text-muted-foreground">
                {(() => {
                  const count = Math.min(Math.max(parseInt(bedQuantity, 10) || 1, 1), 50);
                  const numbers = generateBedNumbers(bedForm.bed_number || '', count);
                  if (numbers.length <= 1) {
                    return 'Bed number must end in a digit to add multiple beds (e.g. "A-1").';
                  }
                  const preview = numbers.slice(0, 3).join(', ');
                  return `Will create ${numbers.length} beds: ${preview}${numbers.length > 3 ? ', …' : ''}`;
                })()}
              </p>
            )}
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Rent/Day (₹) — blank = ward default (₹{ward.default_rent_per_day})</label>
              <Input type="number" value={bedForm.rent_per_day} onChange={e => setBedForm(f => ({ ...f, rent_per_day: e.target.value }))} className="h-8 text-sm" placeholder="Use ward default" /></div>
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
              <Input value={bedForm.notes} onChange={e => setBedForm(f => ({ ...f, notes: e.target.value }))} className="h-8 text-sm" /></div>
            {error && <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddBedOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={saveBed} disabled={saving}>
              {saving ? 'Saving…' : parseInt(bedQuantity, 10) > 1 ? `Add ${Math.min(Math.max(parseInt(bedQuantity, 10) || 1, 1), 50)} Beds` : 'Add Bed'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function IPDSettingsPage() {
  const { currentUser } = useAuth();
  const { isOwner, hasFeature } = usePermission();
  const [wards, setWards] = useState<Ward[]>([]);
  const [loading, setLoading] = useState(true);
  const [addWardOpen, setAddWardOpen] = useState(false);
  const [wardForm, setWardForm] = useState<WardForm>(emptyWard());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!currentUser?.clinic_id) return;
    setLoading(true);
    const data = await getWards(currentUser.clinic_id);
    setWards(data ?? []);
    setLoading(false);
  }, [currentUser?.clinic_id]);

  useEffect(() => { load(); }, [load]);

  const canManageWards = isOwner || currentUser?.role === 'admin';

  const saveWard = async () => {
    setError('');
    if (!wardForm.name.trim()) { setError('Ward name is required'); return; }
    setSaving(true);
    try {
      await createWard({
        name: wardForm.name, ward_type: wardForm.ward_type,
        default_rent_per_day: parseFloat(wardForm.default_rent_per_day) || 0,
        notes: wardForm.notes || null,
      });
      setAddWardOpen(false); setWardForm(emptyWard()); load();
    } catch (e: any) {
      setError(e?.message || 'Failed to create ward');
    } finally {
      setSaving(false);
    }
  };

  if (!hasFeature('ipd')) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4">
        <BedIcon className="w-8 h-8 text-muted-foreground mb-2" />
        <h2 className="text-base font-semibold mb-1">IPD Not Available</h2>
        <p className="text-sm text-muted-foreground max-w-sm">
          In-patient department management is not included in your current plan. Contact support to upgrade.
        </p>
      </div>
    );
  }

  if (!canManageWards) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4">
        <AlertTriangle className="w-8 h-8 text-muted-foreground mb-2" />
        <h2 className="text-base font-semibold mb-1">Access Restricted</h2>
        <p className="text-sm text-muted-foreground max-w-sm">
          Only the clinic owner or an admin can manage wards and beds.
        </p>
        <Link to="/ipd" className="text-sm text-primary mt-3 hover:underline">Back to IPD</Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b shrink-0">
        <div className="flex items-center gap-2">
          <Link to="/ipd" className="p-1 -ml-1 rounded hover:bg-muted text-muted-foreground" aria-label="Back to IPD">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <h1 className="text-lg font-semibold">Wards & Beds</h1>
        </div>
        <Button size="sm" onClick={() => { setWardForm(emptyWard()); setError(''); setAddWardOpen(true); }}>
          <Plus className="w-4 h-4 mr-1" /> New Ward
        </Button>
      </div>

      <div className="flex-1 overflow-auto px-4 py-4 space-y-3">
        {loading ? (
          <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
        ) : wards.length === 0 ? (
          <div className="text-sm text-muted-foreground py-12 text-center">No wards configured. Add your first ward.</div>
        ) : (
          wards.map(w => <WardCard key={w.id} ward={w} onRefresh={load} />)
        )}
      </div>

      <Dialog open={addWardOpen} onOpenChange={setAddWardOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>New Ward</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Name *</label>
              <Input value={wardForm.name} onChange={e => setWardForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. General Ward, ICU" className="h-8 text-sm" /></div>
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Type</label>
              <Sel value={wardForm.ward_type} onChange={v => setWardForm(f => ({ ...f, ward_type: v }))} options={WARD_TYPES} /></div>
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Default Rent/Day (₹)</label>
              <Input type="number" value={wardForm.default_rent_per_day} onChange={e => setWardForm(f => ({ ...f, default_rent_per_day: e.target.value }))} className="h-8 text-sm" /></div>
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
              <Input value={wardForm.notes} onChange={e => setWardForm(f => ({ ...f, notes: e.target.value }))} className="h-8 text-sm" /></div>
            {error && <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddWardOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={saveWard} disabled={saving}>{saving ? 'Saving…' : 'Create Ward'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
